import { useMemo, useState } from 'react'
import { filter } from 'lodash-es'
import { Box, Button, Stack } from '@mui/material'
import {
  DataGridPremium,
  GRID_CHECKBOX_SELECTION_FIELD,
  GridFilterModel,
  GridLogicOperator,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium'
import { useControlStore, useSystemManagerStore } from '@editorStores'
import { assemblyTypes } from 'src/components/manager/assemblies/constants'

const usageTypeShortNames: Record<AssemblyTypes, string> = {
  Inner: 'IW',
  Outer: 'AW',
  Ceiling: 'Decke',
  Roof: 'Dach',
}

interface Props {
  onSelectElements: (elementGuids: string[]) => void
}

function QuickSearchToolbar() {
  return <GridToolbarQuickFilter size="small" fullWidth />
}

const ColoringAssemblies = ({ onSelectElements }: Props) => {
  const [activeFilters, setActiveFilters] = useState<Set<AssemblyTypes>>(
    new Set(['Inner', 'Outer']),
  )
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => ({
    items: [
      {
        field: 'usage_type',
        operator: 'isAnyOf',
        value: ['Inner', 'Outer'],
      },
    ],
    quickFilterExcludeHiddenColumns: false,
    quickFilterLogicOperator: GridLogicOperator.And,
  }))

  const assemblies = useSystemManagerStore(state => state.assemblies)
  const assemblyAssignment = useSystemManagerStore(state => state.assemblyAssignment)

  const assemblyRows = useMemo(() => {
    return assemblies.map(assembly => {
      const numElements = assemblyAssignment.filter(
        assignment => assignment.assembly_guid === assembly.guid,
      ).length

      return {
        ...assembly,
        id: assembly.guid,
        name: assembly.name,
        shortName: assembly.short_name,
        numElements,
        usageTypeLabel: usageTypeShortNames[assembly.usage_type],
      }
    })
  }, [assemblies, assemblyAssignment])

  const selectedAssemblies = useControlStore(state => state.selectedAssembliesForColoring)
  const setSelectedAssemblies = useControlStore(state => state.setSelectedAssembliesForColoring)

  const toggleFilter = (usageType: AssemblyTypes) => {
    setActiveFilters(prev => {
      const newFilters = new Set(prev)
      if (newFilters.has(usageType)) {
        newFilters.delete(usageType)
      } else {
        newFilters.add(usageType)
      }

      // Update filter model with new button filters
      setFilterModel(currentModel => {
        const otherItems = currentModel.items.filter(item => item.field !== 'usage_type')
        const newItems =
          newFilters.size > 0
            ? [
                ...otherItems,
                {
                  field: 'usage_type',
                  operator: 'isAnyOf',
                  value: Array.from(newFilters),
                },
              ]
            : otherItems

        return {
          ...currentModel,
          items: newItems,
        }
      })

      return newFilters
    })
  }

  const handleFilterModelChange = (newModel: GridFilterModel) => {
    setFilterModel(newModel)
  }

  const selectedElements = useMemo(
    () =>
      filter(assemblyAssignment, assignment =>
        selectedAssemblies.has(assignment.assembly_guid),
      ).map(assignment => assignment.element_guid),
    [assemblyAssignment, selectedAssemblies],
  )

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        {assemblyTypes.map(assemblyType => (
          <Button
            key={assemblyType.usage_type}
            size="small"
            variant={activeFilters.has(assemblyType.usage_type) ? 'contained' : 'outlined'}
            onClick={() => toggleFilter(assemblyType.usage_type)}
            sx={{ paddingX: 1, paddingY: 0 }}
          >
            {assemblyType.label}
          </Button>
        ))}
      </Stack>

      <DataGridPremium
        rows={assemblyRows}
        columns={[
          {
            field: GRID_CHECKBOX_SELECTION_FIELD,
            width: 40,
            minWidth: 30,
            align: 'center',
            headerAlign: 'center',
            display: 'flex',
            sortable: false,
            renderHeaderFilter: () => null,
          },
          {
            field: 'numElements',
            headerName: 'Anz.',
            width: 45,
            minWidth: 40,
            align: 'right',
            renderHeaderFilter: () => null,
          },
          {
            field: 'usage_type',
            headerName: 'Typ',
            display: 'flex',
            type: 'string',
            renderHeaderFilter: () => null,
          },
          {
            field: 'usageTypeLabel',
            headerName: 'Typ',
            display: 'flex',
            width: 50,
            renderHeaderFilter: () => null,
          },
          {
            field: 'shortName',
            headerName: 'Kurzbez.',
            display: 'flex',
            width: 170,
          },
          {
            field: 'name',
            headerName: 'Name',
            flex: 1,
          },
        ]}
        checkboxSelection
        rowSelectionModel={Array.from(selectedAssemblies)}
        onRowSelectionModelChange={newSelection => {
          setSelectedAssemblies(new Set(newSelection as string[]))
        }}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        columnHeaderHeight={35}
        getRowHeight={() => 25}
        slots={{
          toolbar: QuickSearchToolbar,
        }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              usage_type: false,
            },
          },
          sorting: {
            sortModel: [{ field: 'numElements', sort: 'desc' }],
          },
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25]}
        pagination
        headerFilters
        headerFilterHeight={70}
        density="compact"
        sx={{
          '& .MuiDataGrid-footerContainer': {
            minHeight: '25px',
            maxHeight: '25px',
          },
        }}
      />

      <Stack direction="row" spacing={1} p={1} justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={selectedElements.length === 0}
          onClick={() => {
            onSelectElements(selectedElements)
          }}
        >
          Auswählen
        </Button>
      </Stack>
    </Box>
  )
}

export default ColoringAssemblies
